import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { startLoading, stopLoading } from "./LoadingSlice";
import api from "../../api";
import Cookies from "js-cookie";
import { closeUsernameSetupModal } from "./ModalSlice";
import {
  playerLogin,
  playerMpinLogin,
  playerRefresh,
} from "../../api/services/auth";
import { API_APP_ID } from "../../config";
import { registerPlayer, sendRegisterOtp, verifyRegisterOtp } from "../../api/services/registration";
import { resetPlayerMpin, setPlayerMpin } from "../../api/services/mpin";
import {
  getFavoriteGamesRequest,
  getLastPlayedGamesRequest,
  getPlayerMe,
  getPlayerBalance,
  updatePlayerMe,
  addFavouriteGameRequest,
  removeFavouriteGameRequest,
} from "../../api/services/player";
import {
  changePlayerPassword,
  getPlayerLoginHistory,
} from "../../api/services/security";
import { normalizePlayer } from "../../utils/normalizePlayer";
import { buildLoginPayload } from "../../utils/buildLoginPayload";
import {
  buildRegisterPayload,
  formatRegisterPhone,
} from "../../utils/buildRegisterPayload";
import {
  buildMpinLoginPayload,
  buildMpinPayload,
  buildResetMpinPayload,
} from "../../utils/buildMpinPayload";
import {
  clearAuthSession,
  enforceSessionScope,
  ensureAuthTokenFromResponses,
  extractAuthToken,
  getStoredRefreshToken,
  hasAuthToken,
  hasRestorableSession,
  persistAuthSession,
  persistAuthToken,
  persistRefreshToken,
} from "../../utils/authToken";
import { clearLocalStoragePreservingAppKeys } from "../../utils/rememberMe";
import {
  getGuestFavGames,
  getGuestLastPlayedGames,
  recordGuestLastPlayed,
  saveGuestFavGames,
  saveGuestLastPlayedGames,
  toggleGuestFavourite,
} from "../../utils/guestGames";

function getApiErrorMessage(errData, fallback = "Request failed") {
  if (!errData) return fallback;
  if (typeof errData === "string") return errData;
  return (
    errData.error_description ||
    errData.detail ||
    errData.message ||
    errData.error ||
    fallback
  );
}

/**
 * Backend gaps: the legacy CRM endpoints listed below are not exposed by the
 * player API yet. The thunks that used them short-circuit instead of firing a
 * request that 404s and surfaces as an unrelated error, so every screen shows
 * one clear "not available" notice. Flip a flag to false once the matching
 * endpoint is wired up.
 */
export const UNAVAILABLE_FEATURES = {
  mobileOtp: true, // POST /otp/sendOTP, POST /otp/verifyOTP
  updateUser: true, // POST /users/updateUser
  updateEmail: true, // PUT /player/me has no email field and no email endpoint exists
};

export const FEATURE_UNAVAILABLE_CODE = "FEATURE_UNAVAILABLE";

/** One consistent wording for every screen that hits a missing endpoint. */
export const featureUnavailableError = (feature = "This feature") => ({
  code: FEATURE_UNAVAILABLE_CODE,
  error: `${feature} is not available yet. Please check back later.`,
});

// Define the async thunk action creator

// Set the expiration time in minutes

export const createUser = createAsyncThunk(
  "user/createUser",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoading());
    try {
      const { registration } = getState().appConfig;
      const defaults = registration?.defaults ?? {};
      const registerBody = await buildRegisterPayload(payload, defaults);
      const response = await registerPlayer(registerBody);
      if (response?.success === false) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      const errData = error.response?.data;
      return rejectWithValue(
        errData || { error: error.message || "Registration failed" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);

/** Register player. OTP is sent separately after the user confirms. */
export const registerAndSendOtp = createAsyncThunk(
  "user/registerAndSendOtp",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoading());
    try {
      const { registration } = getState().appConfig;
      const defaults = registration?.defaults ?? {};
      const auth = registration?.auth ?? {};
      const registerBody = await buildRegisterPayload(payload, defaults);
      const registerResponse = await registerPlayer(registerBody);

      if (registerResponse?.success === false) {
        return rejectWithValue(registerResponse);
      }

      if (auth.player_otp_enabled === false) {
        return { registerResponse, skipOtp: true };
      }

      const fullPhone = formatRegisterPhone(payload.countryCode, payload.phone);
      if (!fullPhone) {
        return rejectWithValue({
          error: "A valid phone number is required for verification",
        });
      }

      return { registerResponse, skipOtp: false, phone: fullPhone };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Registration failed" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const sendRegisterPhoneOtp = createAsyncThunk(
  "user/sendRegisterPhoneOtp",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(startLoading());
      dispatch(resetMessage());
      const response = await sendRegisterOtp(payload);
      if (response?.success === false) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: "Failed to send OTP" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const sendMobileOTP = createAsyncThunk(
  "user/sendOTP",
  async (payload, { dispatch, rejectWithValue }) => {
    const { flowSource, ...otpPayload } = payload || {};
    if (UNAVAILABLE_FEATURES.mobileOtp) {
      dispatch(resetMessage());
      return rejectWithValue(featureUnavailableError("Mobile OTP verification"));
    }
    try {
      dispatch(startLoading());
      dispatch(resetMessage());
      const response = await api.post(`/otp/sendOTP`, otpPayload);
      return { ...response.data, flowSource: flowSource || "login" };
    } catch (error) {
      // Handle error condition
      throw Error("Error fetching user data");
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const verifyRegisterPhoneOtp = createAsyncThunk(
  "user/verifyRegisterPhoneOtp",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoading());
    try {
      const response = await verifyRegisterOtp(payload);
      if (response?.success === false) {
        return rejectWithValue(response);
      }

      ensureAuthTokenFromResponses(response, getState().user.data);

      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: "OTP verification failed" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const validateMobileOTP = createAsyncThunk(
  "user/validateOTP",
  async (payload, { dispatch, rejectWithValue }) => {
    if (UNAVAILABLE_FEATURES.mobileOtp) {
      return rejectWithValue(featureUnavailableError("Mobile OTP verification"));
    }
    dispatch(startLoading());
    try {
      const response = await api.post(`/otp/verifyOTP`, payload.data);

      return {
        response: response.data,
        fromForgotPassword: payload.fromForgotPassword,
      };
    } catch (error) {
      // Handle error condition
      throw rejectWithValue(error.response.data);
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const setupMpin = createAsyncThunk(
  "user/setupMpin",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    ensureAuthTokenFromResponses(getState().user.data);

    if (!hasAuthToken()) {
      return rejectWithValue({
        error:
          "Cannot set MPIN: register and verify-otp did not return an access token, but /player/mpin/set requires Authorization header.",
        code: "AUTH_REQUIRED",
      });
    }

    dispatch(startLoading());
    try {
      const mpinBody = await buildMpinPayload(payload.mpin);
      const response = await setPlayerMpin(mpinBody);
      if (response?.success === false) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: "Failed to set MPIN" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const sendResetMpinOtp = createAsyncThunk(
  "user/sendResetMpinOtp",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    const { registration } = getState().appConfig;
    const auth = registration?.auth ?? {};
    const phone =
      payload?.phone ||
      formatRegisterPhone(payload?.countryCode, payload?.phoneNumber);

    if (!phone) {
      return rejectWithValue({
        error: "Please enter a valid phone number",
      });
    }

    dispatch(startLoading());
    try {
      const response = await sendRegisterOtp({
        phone,
        action: "reset_mpin",
        delivery_method: auth.default_delivery_method || "sms",
      });

      if (response?.success === false) {
        return rejectWithValue(response);
      }

      return { ...response, phone };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: "Failed to send OTP" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const resetMpin = createAsyncThunk(
  "user/resetMpin",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    ensureAuthTokenFromResponses(getState().user.data);

    if (!hasAuthToken()) {
      return rejectWithValue({
        error: "Authentication required. Please log in again.",
        code: "AUTH_REQUIRED",
      });
    }

    dispatch(startLoading());
    try {
      const mpinBody = await buildResetMpinPayload(payload.mpin, {
        password: payload.password,
        otp: payload.otp,
        authMethod: payload.authMethod || "password",
      });
      const response = await resetPlayerMpin(mpinBody);
      if (response?.success === false) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: "Failed to reset MPIN" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const getUserData = createAsyncThunk(
  "user/getUserData",
  async (payload, { dispatch, rejectWithValue }) => {
    if (!hasAuthToken()) {
      return { data: {} };
    }

    if (payload.showLoader) dispatch(startLoading());
    try {
      const response = await getPlayerMe();
      if (response?.success === false) {
        return rejectWithValue(response);
      }
      const normalized = normalizePlayer(response?.data || {});
      try {
        localStorage.setItem("player", JSON.stringify(response?.data || {}));
      } catch {
        // localStorage may be unavailable
      }
      return { data: normalized };
    } catch (error) {
      throw rejectWithValue(
        error.response?.data || { error: error.message || "Failed to load profile" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);

/** Balance card for the withdraw screen (GET /player/balance). */
export const getBalanceCard = createAsyncThunk(
  "user/getBalanceCard",
  async (_, { rejectWithValue }) => {
    if (!hasAuthToken()) return rejectWithValue({ error: "Not logged in" });
    try {
      const response = await getPlayerBalance();
      if (response?.success === false) return rejectWithValue(response);
      const data = response?.data || {};
      return {
        currency: data.currency || "INR",
        totalBalance: Number(data.total_balance) || 0,
        withdrawableBalance: Number(data.withdrawable_balance) || 0,
        bonusBalance: Number(data.bonus_balance) || 0,
        realWalletFrozen: Boolean(data.real_wallet_frozen),
        wageringProgressPct: data.wagering_progress_pct ?? null,
        activeBonus: data.active_bonus ?? null,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: error.message || "Failed to load balance" }
      );
    }
  }
);

/** Form field -> PUT /player/me field, for the names that differ. */
const PROFILE_API_FIELDS = {
  firstName: "first_name",
  lastName: "last_name",
  dob: "date_of_birth",
};

export const updatePlayerProfile = createAsyncThunk(
  "user/updatePlayerProfile",
  async (fields, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const payload = Object.entries(fields).reduce((body, [field, value]) => {
        body[PROFILE_API_FIELDS[field] || field] = value;
        return body;
      }, {});
      const response = await updatePlayerMe(payload);
      if (response?.success === false) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          error: getApiErrorMessage(error.message, "Failed to update profile"),
        }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (payload, { dispatch, rejectWithValue }) => {
    if (UNAVAILABLE_FEATURES.updateUser) {
      return rejectWithValue(featureUnavailableError());
    }
    dispatch(startLoading());
    try {
      const response = await api.post(`/users/updateUser`, payload);
      // Callers refresh the profile themselves via getUserData so a failed
      // refetch cannot log the user out on an otherwise successful update.
      return response.data;
    } catch (error) {
      // Handle error condition
      return rejectWithValue(
        error.response?.data || {
          error: getApiErrorMessage(error.message, "Failed to update profile"),
        }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (payload, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoading());
    try {
      const isMpinLogin = payload.grantType === "mpin";
      const loginBody = isMpinLogin
        ? await buildMpinLoginPayload(payload)
        : await buildLoginPayload(payload);
      const response = isMpinLogin
        ? await playerMpinLogin(loginBody)
        : await playerLogin(loginBody);

      if (response?.success === false) {
        return rejectWithValue(
          response || { error: "Login failed" }
        );
      }

      return response;
    } catch (error) {
      const errData = error.response?.data;
      if (
        errData?.code == 422 &&
        typeof errData?.error === "string" &&
        errData.error.includes("Refresh Token")
      ) {
        const creds = getState().user.userCreds;
        dispatch(loginUser(creds));
        return;
      }
      throw rejectWithValue(
        errData || { error: error.message || "Login failed" }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
// the home carousels render their own empty state, so no blocking loader
export const getFavGames = createAsyncThunk(
  "user/favGames",
  async (_, { rejectWithValue }) => {
    try {
      return await getFavoriteGamesRequest();
    } catch (error) {
      return rejectWithValue(error.response?.data || {});
    }
  }
);
export const getLastPlayedGames = createAsyncThunk(
  "user/astPlayedGame",
  async (_, { rejectWithValue }) => {
    try {
      return await getLastPlayedGamesRequest();
    } catch (error) {
      return rejectWithValue(error.response?.data || {});
    }
  }
);
// pass the whole game so the lists can be updated locally without refetching
const favouriteThunk = (name, request, isFavorite) =>
  createAsyncThunk(
    `user/${name}`,
    async ({ gameId, game }, { rejectWithValue }) => {
      try {
        await request(gameId);
        return { gameId, game, isFavorite };
      } catch (error) {
        return rejectWithValue(error.response?.data || {});
      }
    }
  );
export const addFavourite = favouriteThunk(
  "addFavourite",
  addFavouriteGameRequest,
  true
);
export const removeFavourite = favouriteThunk(
  "removeFavourite",
  removeFavouriteGameRequest,
  false
);
export const makeFavourite = addFavourite;
export const getLoginLogs = createAsyncThunk(
  "user/getLoginLogs",
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      return await getPlayerLoginHistory({ limit: 20 });
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          error: getApiErrorMessage(error.message, "Failed to load login history"),
        }
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);

/**
 * Security screen password change. Rejects with a display-ready message so
 * the form can show it inline without touching the shared `user.error`.
 */
export const changePassword = createAsyncThunk(
  "user/changePassword",
  async (
    { currentPassword, newPassword, confirmPassword },
    { dispatch, rejectWithValue }
  ) => {
    dispatch(startLoading());
    try {
      return await changePlayerPassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
    } catch (error) {
      const errData = error.response?.data;
      const code = errData?.code || errData?.error?.code || errData?.error;
      if (code === "INVALID_CURRENT_PASSWORD") {
        return rejectWithValue("Current password is incorrect");
      }
      return rejectWithValue(
        getApiErrorMessage(
          typeof errData?.error === "object" ? errData.error : errData,
          "Failed to change password"
        )
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const logoutUser = createAsyncThunk(
  "users/logout",
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      // const response = await api.post(`/users/logout`);
      // return response.data;

      clearAuthSession();
      clearLocalStoragePreservingAppKeys();
      sessionStorage.clear();

      return { status: 200 };
    } catch (error) {
      // Handle error condition
      throw rejectWithValue(error.response.data);
    } finally {
      dispatch(stopLoading());
    }
  }
);

/**
 * Renews the access token with POST /api/v1/auth/player-refresh.
 * Drives the "Keep Playing" action on the session-timeout popup: the new token
 * and its expiry are written to cookies by the fulfilled reducer, which is what
 * pushes the timeout warning out.
 *
 * TODO: also call this on boot when isRememberMeEnabled() && !hasAuthToken(),
 * so a returning player is signed back in instead of landing on the login page.
 */
export const refreshJwtToken = createAsyncThunk(
  "user/refreshToken",
  async (_, { rejectWithValue }) => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      return rejectWithValue({
        error: "Your session has expired. Please log in again.",
      });
    }

    try {
      const response = await playerRefresh({
        refreshToken,
        app_id: API_APP_ID,
      });

      if (response?.success === false) {
        return rejectWithValue(response);
      }

      // Without a new access token the session is not actually extended.
      if (!extractAuthToken(response)) {
        return rejectWithValue({
          error: "Session could not be extended. Please log in again.",
        });
      }

      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          error: error.message || "Session refresh failed",
        }
      );
    }
  }
);

export const getAstroData = createAsyncThunk(
  "user/getAstroData",
  async (showLoader = true, { dispatch, rejectWithValue }) => {
    if (showLoader) dispatch(startLoading());
    try {
      const response = await api.get(`/users/getAstroData`);
      return response.data;
    } catch (error) {
      // Handle error condition
      throw rejectWithValue(error.response.data);
    } finally {
      dispatch(stopLoading());
    }
  }
);
// Create a slice for your API-related actions and reducers
const userSlice = createSlice({
  name: "user",
  initialState: {
    data: null,
    otpMessage: "",
    error: null,
    otpVerified: false,
    userLoggedIn: false,
    userData: {},
    // GET /player/balance; null until the withdraw screen loads it
    balanceCard: null,
    // guest lists until checkUserLogin / the API replace them
    favGames: getGuestFavGames(),
    lastPlayedGames: getGuestLastPlayedGames(),
    madeFavourite: false,
    madeUnFavourite: false,
    userUpdated: false,
    loginLogs: [],
    otpSent: false,
    otpFlowSource: null,
    phoneDetails: {},
    showAlreadyLoginMessage: true,
    alreadyLoggedData: {},
    loggedOut: false,
    showKeepWorkingPopup: false,
    expired: false,
    totalRecords: 0,
    continueSession: false,
    tokenRefreshed: false,
    userCreds: {},
    astroData: {},
  },
  reducers: {
    relogin(state) {
      state.userLoggedIn = false;
      state.error = null;
      state.userData = {};
      state.favGames = getGuestFavGames();
      state.lastPlayedGames = getGuestLastPlayedGames();
      state.otpVerified = false;
      state.otpMessage = "";
      state.data = null;
      state.userUpdated = false;
      state.otpSent = false;
      state.otpFlowSource = null;
      state.alreadyLoggedData = {};
      state.showAlreadyLoginMessage = true;
      Cookies.remove("token");
      Cookies.remove("playerAccessToken");
      Cookies.remove("tokenExpires");
      // Cookies.remove("refreshToken");
      clearLocalStoragePreservingAppKeys();
      sessionStorage.clear();
    },
    checkUserLogin(state) {
      if (!hasAuthToken()) return;

      // Without remember me the session belongs to the tab that logged in:
      // a reopened tab has no tab marker, so the leftover token is discarded.
      if (hasRestorableSession()) {
        state.userLoggedIn = true;
        // the player's own lists come from the API, not the guest ones
        state.favGames = [];
        state.lastPlayedGames = [];
        return;
      }

      state.userLoggedIn = false;
      enforceSessionScope();
    },
    toggleGuestFavGame(state, action) {
      const { favGames, lastPlayedGames, isFavorite } = toggleGuestFavourite(
        action.payload
      );
      state.favGames = favGames;
      state.lastPlayedGames = lastPlayedGames;
      state.madeFavourite = isFavorite;
      state.madeUnFavourite = !isFavorite;
    },
    addGuestLastPlayed(state, action) {
      state.lastPlayedGames = recordGuestLastPlayed(action.payload);
    },
    hideToast(state) {
      state.madeFavourite = false;
      state.madeUnFavourite = false;
    },
    storePhoneAndCountry(state, action) {
      state.phoneDetails = action.payload;
    },
    saveUserCreds(state, action) {
      state.userCreds = action.payload;
    },
    resetMessage(state) {
      state.otpMessage = "";
    },
    updateBalance(state, action) {
      state.userData.socketBalance = action.payload;
    },
    /** `balance:updated` payload from the balance WebSocket. */
    applyBalanceUpdate(state, action) {
      const data = action.payload || {};
      const total = Number(data.total_balance);
      const available = Number(data.available_balance);
      const bonus = Number(data.bonus_balance);
      if (Number.isFinite(total)) state.userData.totalBalance = total;
      if (Number.isFinite(available)) state.userData.redisBalance = available;
      if (Number.isFinite(bonus)) state.userData.bonus = bonus;
      if (state.balanceCard) {
        if (Number.isFinite(total)) state.balanceCard.totalBalance = total;
        if (Number.isFinite(available))
          state.balanceCard.withdrawableBalance = available;
        if (Number.isFinite(bonus)) state.balanceCard.bonusBalance = bonus;
      }
    },
    setPlayerBalance(state, action) {
      const balance = Number(action.payload);
      if (!Number.isFinite(balance)) return;
      state.userData.totalBalance = balance;
      state.userData.redisBalance = balance;
    },
    hidePopup(state) {
      state.showAlreadyLoginMessage = false;
    },
    showKeepWorkingModal(state) {
      state.showKeepWorkingPopup = true;
    },
    hideKeepWorkingModal(state) {
      state.showKeepWorkingPopup = false;
    },
    tokenExpired(state) {
      state.expired = true;
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUser.pending, (state) => {
        state.otpVerified = false;
        state.otpSent = false;
        state.otpMessage = "";
        state.otpFlowSource = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.data = action.payload;
        const token = extractAuthToken(action.payload);
        if (token && persistAuthToken(token)) {
          state.userLoggedIn = true;
          state.tokenRefreshed = true;
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          (typeof action.payload === "string" ? action.payload : null) ||
          "An unknown error occurred";
      })
      .addCase(registerAndSendOtp.pending, (state) => {
        state.otpVerified = false;
        state.otpSent = false;
        state.otpMessage = "";
        state.otpFlowSource = null;
        state.error = null;
      })
      .addCase(registerAndSendOtp.fulfilled, (state, action) => {
        const { registerResponse, skipOtp } = action.payload;
        state.data = registerResponse;
        state.error = "";

        const token = extractAuthToken(registerResponse);
        if (token && persistAuthToken(token)) {
          state.userLoggedIn = true;
          state.tokenRefreshed = true;
        }

        if (skipOtp) return;

        // OTP is sent after the user confirms on the success prompt
        state.otpSent = false;
        state.otpFlowSource = "register";
        state.otpMessage = "";
      })
      .addCase(registerAndSendOtp.rejected, (state, action) => {
        state.otpSent = false;
        state.otpFlowSource = null;
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          "Registration failed";
      })
      .addCase(sendRegisterPhoneOtp.fulfilled, (state, action) => {
        state.otpSent = true;
        state.otpFlowSource = "register";
        state.error = "";
        state.otpMessage =
          action.payload?.data?.message ||
          action.payload?.message ||
          "OTP sent successfully";
      })
      .addCase(sendRegisterPhoneOtp.rejected, (state, action) => {
        state.otpSent = false;
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          "Failed to send OTP";
      })
      .addCase(sendResetMpinOtp.fulfilled, (state, action) => {
        state.otpSent = true;
        state.otpFlowSource = "reset_mpin";
        state.error = "";
        state.otpMessage =
          action.payload?.data?.message ||
          action.payload?.message ||
          "OTP sent successfully";
      })
      .addCase(sendResetMpinOtp.rejected, (state, action) => {
        state.otpSent = false;
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          "Failed to send OTP";
      })
      .addCase(sendMobileOTP.fulfilled, (state, action) => {
        state.otpSent = true;
        state.otpFlowSource = action.payload.flowSource || "login";
        state.error = "";
        state.otpMessage = action.payload.message || "OTP sent successfully";
      })
      .addCase(verifyRegisterPhoneOtp.pending, (state) => {
        state.otpVerified = false;
        state.error = null;
      })
      .addCase(sendMobileOTP.rejected, (state, action) => {
        state.otpSent = false;
        state.error =
          action.payload?.error ||
          action.error?.message ||
          "User not found with given phone number";
      })
      .addCase(verifyRegisterPhoneOtp.fulfilled, (state, action) => {
        state.otpVerified = true;
        state.error = null;
        state.otpMessage =
          action.payload?.message ||
          action.payload?.data?.message ||
          "Phone number verified successfully";

        // verify-otp returns a full session (access + refresh token, player)
        const { accessToken } = persistAuthSession(action.payload);
        const player = action.payload?.data?.player;
        if (player) {
          state.userData = { ...state.userData, ...player };
        }
        const token = accessToken || extractAuthToken(state.data);
        if (token && persistAuthToken(token)) {
          state.tokenRefreshed = true;
          state.userLoggedIn = true;
        }
      })
      .addCase(verifyRegisterPhoneOtp.rejected, (state, action) => {
        state.otpVerified = false;
        state.otpMessage = "";
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          "OTP verification failed";
      })
      .addCase(validateMobileOTP.fulfilled, (state, action) => {
        state.otpVerified = true;
        state.error = null;
        const token = action.payload.response.data.Authorization;
        if (token) {
          state.tokenRefreshed = true;
          if (action.payload.fromForgotPassword) {
            Cookies.set("tempToken", token);
            return;
          }
          persistAuthToken(token);
        }
        state.userLoggedIn = true;
        // TODO: legacy POST /users/saveLoginLogs — re-enable when player API is wired
        // getUserInfo()
        //   .then(async (data) => {
        //     try {
        //       const res = await api.post(`/users/saveLoginLogs`, data);
        //       console.log(res.data);
        //     } catch (error) {
        //       console.log("error saving login logs");
        //     }
        //   })
        //   .catch((error) =>
        //     console.error("Error retrieving user info:", error)
        //   );
      })
      .addCase(validateMobileOTP.rejected, (state, action) => {
        state.otpVerified = false;
        state.otpMessage = "";
        state.error = action.payload?.error || "OTP validation failed";
      })
      .addCase(setupMpin.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(setupMpin.rejected, (state, action) => {
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          "Failed to set MPIN";
      })
      .addCase(resetMpin.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(resetMpin.rejected, (state, action) => {
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          "Failed to reset MPIN";
      })

      .addCase(loginUser.fulfilled, (state, action) => {
        if (!action.payload) return;

        state.error = null;
        state.userCreds = {};
        state.tokenRefreshed = true;

        const legacyData = action.payload?.data ?? action.payload;
        if (legacyData?.alreadyLoggedIn) {
          state.alreadyLoggedData = {
            browser: legacyData.browser?.split?.(" ")?.[0] || "",
            device: legacyData.device,
            alreadyLoggedIn: legacyData.alreadyLoggedIn,
          };
          const refreshToken =
            legacyData.refreshToken || legacyData.refresh_token;
          persistRefreshToken(refreshToken, action.payload);
          return;
        }

        state.alreadyLoggedData = {};
        const { accessToken } = persistAuthSession(action.payload);
        const player = action.payload?.data?.player;
        if (player) {
          state.userData = { ...state.userData, ...player };
        }
        if (accessToken) {
          state.userLoggedIn = true;
          // TODO: legacy POST /users/saveLoginLogs — re-enable when player API is wired
          // getUserInfo()
          //   .then(async (data) => {
          //     try {
          //       const res = await api.post(`/users/saveLoginLogs`, data);
          //       console.log(res.data);
          //     } catch (error) {
          //       console.log("error saving login logs");
          //     }
          //   })
          //   .catch((error) =>
          //     console.error("Error retrieving user info:", error)
          //   );
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.userLoggedIn = false;
        if (
          action.payload?.code == 422 &&
          typeof action.payload?.error === "string" &&
          action.payload.error.includes("Refresh Token")
        ) {
          return;
        }
        state.error = getApiErrorMessage(action.payload, "Login Failed");
      })
      .addCase(getBalanceCard.fulfilled, (state, action) => {
        state.balanceCard = action.payload;
        // keep the header/profile balances in step with the card
        state.userData.totalBalance = action.payload.totalBalance;
        state.userData.redisBalance = action.payload.withdrawableBalance;
        state.userData.bonus = action.payload.bonusBalance;
      })
      .addCase(getUserData.fulfilled, (state, action) => {
        state.userData = {
          ...state.userData,
          ...(action.payload.data || {}),
        };
      })
      .addCase(getUserData.rejected, (state, action) => {
        state.userLoggedIn = false;
        clearAuthSession();
        clearLocalStoragePreservingAppKeys();
        sessionStorage.clear();
        state.error = action.payload?.error || "Invalid User Request";
      })
      .addCase(updateUser.pending, (state) => {
        // Reset so a second update re-triggers the `userUpdated` watchers.
        state.userUpdated = false;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.error = null;
        // Merge: the update response carries only the changed fields, so
        // replacing would drop balances, kyc flags and the rest of userData.
        state.userData = {
          ...state.userData,
          ...(action.payload?.data || {}),
        };
        state.userUpdated = true;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = getApiErrorMessage(action.payload, "Request Failed");
      })
      .addCase(getFavGames.fulfilled, (state, action) => {
        state.favGames = action.payload.data || [];
        saveGuestFavGames(state.favGames);
      })
      .addCase(getFavGames.rejected, (state, action) => {
        state.error = action.payload?.error || "Invalid User Request";
      })
      .addCase(getLastPlayedGames.fulfilled, (state, action) => {
        state.lastPlayedGames = action.payload.data || [];
        saveGuestLastPlayedGames(state.lastPlayedGames);
      })
      .addCase(getLastPlayedGames.rejected, (state, action) => {
        state.error = action.payload?.error || "Invalid Request";
      })
      .addCase(getLoginLogs.fulfilled, (state, action) => {
        state.loginLogs = action.payload?.data || [];
      })
      .addCase(getLoginLogs.rejected, (state, action) => {
        state.loginLogs = [];
        state.error = getApiErrorMessage(action.payload, "Invalid Request");
      })
      .addCase(logoutUser.fulfilled, (state, action) => {
        state.loggedOut = action.payload.status == 200;
        state.userLoggedIn = false;
        // Cookies.remove("refreshToken");
        Cookies.remove("tokenExpires");
        Cookies.remove("token");
        window.location.reload();
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload?.error || "Request Failed";
      })
      .addCase(refreshJwtToken.pending, (state) => {
        state.tokenRefreshed = false;
      })
      .addCase(refreshJwtToken.fulfilled, (state, action) => {
        state.error = null;
        state.tokenRefreshed = true;
        state.expired = false;
        const { accessToken } = persistAuthSession(action.payload);
        if (accessToken) state.userLoggedIn = true;
      })
      .addCase(refreshJwtToken.rejected, (state, action) => {
        state.tokenRefreshed = false;
        state.error = getApiErrorMessage(action.payload, "Request Failed");
      })
      .addCase(getAstroData.pending, (state) => {
        state.astroData = {};
      })
      .addCase(getAstroData.fulfilled, (state, action) => {
        state.astroData = action.payload.data;
      })
      .addCase(getAstroData.rejected, (state, action) => {
        state.error = action.payload?.error || "Request Failed";
      })
      .addMatcher(
        (action) =>
          [addFavourite.fulfilled.type, removeFavourite.fulfilled.type].includes(
            action.type
          ),
        (state, action) => {
          const { gameId, game, isFavorite } = action.payload;
          const sameGame = (g) => String(g.id) === String(gameId);
          state.lastPlayedGames = state.lastPlayedGames.map((g) =>
            sameGame(g) ? { ...g, is_favorite: isFavorite } : g
          );
          const others = state.favGames.filter((g) => !sameGame(g));
          state.favGames =
            isFavorite && game
              ? [{ ...game, is_favorite: true }, ...others]
              : others;
          saveGuestFavGames(state.favGames);
          saveGuestLastPlayedGames(state.lastPlayedGames);
          state.madeFavourite = isFavorite;
          state.madeUnFavourite = !isFavorite;
        }
      )
      .addMatcher(
        (action) =>
          [addFavourite.rejected.type, removeFavourite.rejected.type].includes(
            action.type
          ),
        (state, action) => {
          state.error =
            action.payload?.message ||
            action.payload?.error ||
            "Request Failed";
        }
      );
  },
});

// Export the async thunk action creator and the reducer
export const {
  relogin,
  checkUserLogin,
  toggleGuestFavGame,
  addGuestLastPlayed,
  hideToast,
  storePhoneAndCountry,
  resetMessage,
  updateBalance,
  applyBalanceUpdate,
  setPlayerBalance,
  hidePopup,
  showKeepWorkingModal,
  hideKeepWorkingModal,
  tokenExpired,
  resetError,
  saveUserCreds,
} = userSlice.actions;
export const { reducer } = userSlice;
export default userSlice.reducer;
